/**
 * EpiForms
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useReducer, useRef, useEffect } from 'react';
import { useForceUpdate, useFormRenderer, usePrevious, } from 'ui-component-library/hooks';
import { getInitialFormValues, getStepFields } from './EpiForms.helpers';
import { submitForm } from './EpiForms.submit';
import { Form } from 'ui-component-library/base/Form';
import { Text } from 'ui-component-library/base';
import FormStep from './FormStep';
import FormMessage from './FormMessage';
var initialState = {
    isLoading: false,
    invalidFields: [],
    validationMessages: {},
    successMessage: '',
    currentStep: 0,
    values: {},
    submissionId: '',
    serverValidationError: false,
};
var reducer = function (state, action) {
    switch (action.type) {
        case 'UPDATE_STATE':
            return __assign(__assign({}, state), action.payload);
        default:
            return state;
    }
};
/** Rendering of an EpiForms block. */
var EpiForms = function (_a) {
    var fields = _a.fields, _b = _a.method, method = _b === void 0 ? 'GET' : _b, action = _a.action, _c = _a.encodingType, encodingType = _c === void 0 ? 'multipart/form-data' : _c, steps = _a.steps, dependencies = _a.dependencies, id = _a.id, title = _a.title, description = _a.description, confirmationMessage = _a.confirmationMessage, redirectUrl = _a.redirectUrl, onEpiFormSubmit = _a.onEpiFormSubmit, requestVerificationToken = _a.requestVerificationToken;
    var _d = useReducer(reducer, initialState), state = _d[0], dispatch = _d[1];
    var currentStep = state.currentStep, successMessage = state.successMessage, submissionId = state.submissionId, invalidFields = state.invalidFields;
    var forceUpdate = useForceUpdate();
    var _e = useFormRenderer(fields, state, dependencies, id), renderFormElement = _e[0], fieldActionExists = _e[1];
    var showForm = !confirmationMessage && !successMessage;
    var formValues = getInitialFormValues(fields, currentStep);
    var messageHolderRef = useRef(null);
    var prevInvalidFields = usePrevious(invalidFields);
    var prevStep = usePrevious(currentStep);
    if (prevStep === undefined) {
        prevStep = 0;
    }
    // Focus messageHolderRef if success or error
    useEffect(function () {
        var successfullySubmitted = invalidFields.length > 0 || confirmationMessage || successMessage;
        var formMessageElement = messageHolderRef && messageHolderRef.current;
        if (successfullySubmitted &&
            formMessageElement &&
            prevInvalidFields !== invalidFields) {
            messageHolderRef.current.focus();
        }
    }, [invalidFields, messageHolderRef]);
    // Jump back to top if step changes
    useEffect(function () {
        if (window && prevStep !== currentStep && steps.length > 2) {
            window.location.href = "#" + id;
        }
    }, [currentStep]);
    // If we have dependencies (conditional fields), we re-render.
    var onChange = function (field, values) {
        if ((dependencies && dependencies.length > 0) || fieldActionExists) {
            dispatch({
                type: 'UPDATE_STATE',
                payload: { values: values },
            });
            if (dependencies && dependencies.length > 0) {
                forceUpdate();
            }
        }
    };
    var onSubmit = function (values, stepIndex, currentValidationMessages, currentInvalidFields) {
        if (currentInvalidFields && currentInvalidFields.length > 0) {
            dispatch({
                type: 'UPDATE_STATE',
                payload: {
                    invalidFields: currentInvalidFields,
                    validationMessages: currentValidationMessages,
                },
            });
            return;
        }
        submitForm(values, stepIndex, dispatch, steps, {
            currentStep: currentStep,
            submissionId: submissionId,
            action: action,
            method: method,
            redirectUrl: redirectUrl,
            requestVerificationToken: requestVerificationToken,
        });
        if (onEpiFormSubmit) {
            onEpiFormSubmit(values);
        }
    };
    var renderField = function (fieldName) { return (React.createElement("div", { className: "mb-4", key: fieldName }, renderFormElement(fieldName))); };
    return (React.createElement(Form, { initialValues: formValues, sendDataType: "formdata", method: method, action: action, enctype: encodingType, onSubmit: onSubmit, onChange: onChange, multiStep: steps.length > 2, currentStep: currentStep, getStepFields: getStepFields, id: id },
        React.createElement("header", null,
            title && React.createElement(Text, { as: "h2" }, title),
            description && React.createElement(Text, { as: "p" }, description),
            React.createElement(FormMessage, { ref: messageHolderRef, confirmationMessage: confirmationMessage, state: state })),
        showForm && (React.createElement("div", null, steps.map(function (step, i) {
            return step.index === -1 ? (React.createElement("div", { id: "form-" + id + "-hiddenfields", key: "-1", hidden: true }, step.fields.map(renderField))) : steps.length === 2 ? (step.fields.map(renderField)) : (React.createElement(FormStep, { key: i, step: step, nrOfSteps: steps.length, formId: id, currentStep: currentStep, onPrevious: function (state) {
                    dispatch({
                        type: 'UPDATE_STATE',
                        payload: state,
                    });
                } }, step.fields.map(renderField)));
        })))));
};
export default EpiForms;
